import type { Chapter } from '@/types'
import { mergeChapterServerMetadata } from '@/utils/mergeChapterServerMetadata'
import {
  hasUnconfirmedContentChanges,
  hasUnconfirmedTitleChanges,
} from '@/utils/chapterEditorRevisions'
import { readServerContentVersion } from '@/utils/chapterContentVersion'

export type ApplyServerChapterOptions = {
  projectId: string
  chapterId: string
  /** When true, jsonContent/title from the server replace local fields. */
  applyContentFields?: boolean
}

/**
 * Merges server chapter fields into local editor state (PATCH/GET rules).
 */
export function applyServerChapterToEditor(
  local: Chapter,
  server: Chapter,
  options: ApplyServerChapterOptions,
): Chapter {
  const applyContent = options.applyContentFields === true
    || (
      !hasUnconfirmedContentChanges(options.projectId, options.chapterId)
      && !hasUnconfirmedTitleChanges(options.projectId, options.chapterId)
    )

  if (!applyContent) {
    return mergeChapterServerMetadata(local, server)
  }

  return {
    ...mergeChapterServerMetadata(local, server),
    title: server.title ?? local.title,
    jsonContent: server.jsonContent ?? local.jsonContent,
    content: server.content ?? local.content,
  }
}

export function shouldApplyServerContentOnGet(
  projectId: string,
  chapterId: string,
  baseContentVersion: number,
  serverChapter: Chapter,
): boolean {
  if (hasUnconfirmedContentChanges(projectId, chapterId)) {
    return false
  }
  return readServerContentVersion(serverChapter) > baseContentVersion
}
